// Exhaustive union of modifier keys present in race/personality modifier maps.
// Add a new key here AND to MODIFIER_LABELS in the same changeset.
export type ModifierKey =
  | 'trainingCostMod'
  | 'trainingTimeMod'
  | 'draftCostMod'
  | 'constructionCostMod'
  | 'buildingEfficiencyBonus'
  | 'scienceMod'
  | 'wpaMod'
  | 'tpaMod';

export const MODIFIER_LABELS: Record<ModifierKey, string> = {
  trainingCostMod:         'Training cost',
  trainingTimeMod:         'Training time',
  draftCostMod:            'Draft cost',
  constructionCostMod:     'Construction cost',
  buildingEfficiencyBonus: 'Building efficiency',
  scienceMod:              'Science effectiveness',
  wpaMod:                  'Wizard power (WPA)',
  tpaMod:                  'Thief power (TPA)',
};

// Keys where a 0.0 modifier means "Free" rather than −100%.
const FREE_AT_ZERO_KEYS: ReadonlySet<ModifierKey> = new Set([
  'constructionCostMod',
]);

// Cost/time keys where a lower value is favorable (−30% training cost = good).
const LOWER_IS_BETTER_KEYS: ReadonlySet<ModifierKey> = new Set([
  'trainingCostMod',
  'trainingTimeMod',
  'draftCostMod',
  'constructionCostMod',
]);

export interface FormattedModifier {
  label: string;
  display: string;
  sign: 'positive' | 'negative' | 'neutral';
}

/**
 * Formats a (key, value) modifier pair for display.
 * Returns null for unknown keys or value === 1.0 (no effect).
 *
 * Sign convention: the returned sign reflects favorability from the
 * player's perspective (lower cost = positive), while display shows
 * the literal delta percentage.
 */
export function formatModifierValue(
  key: string,
  value: number
): FormattedModifier | null {
  if (!(key in MODIFIER_LABELS)) return null;
  const k = key as ModifierKey;

  if (FREE_AT_ZERO_KEYS.has(k) && value === 0) {
    return { label: MODIFIER_LABELS[k], display: 'Free', sign: 'positive' };
  }

  const deltaPct = Math.round((value - 1) * 100);
  if (deltaPct === 0) return null;

  const favorable = LOWER_IS_BETTER_KEYS.has(k) ? deltaPct < 0 : deltaPct > 0;
  const sign: FormattedModifier['sign'] = favorable ? 'positive' : 'negative';
  const display = deltaPct > 0 ? `+${deltaPct}%` : `−${Math.abs(deltaPct)}%`;
  return { label: MODIFIER_LABELS[k], display, sign };
}
